import { io } from 'socket.io-client';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

function isLoopbackUrl(value) {
    if (!value) return false;

    try {
        const parsed = new URL(value, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return LOOPBACK_HOSTS.has(String(parsed.hostname || '').toLowerCase());
    } catch {
        return false;
    }
}

function resolveSocketUrl() {
    const configuredUrl = process.env.REACT_APP_SOCKET_IO_URL || process.env.REACT_APP_API_URL || '';
    if (!configuredUrl) return undefined;
    if (typeof window === 'undefined') return configuredUrl;

    const currentHost = String(window.location.hostname || '').toLowerCase();
    const currentHostIsLoopback = LOOPBACK_HOSTS.has(currentHost);

    if (!currentHostIsLoopback && isLoopbackUrl(configuredUrl)) {
        return undefined;
    }

    return configuredUrl;
}

const SOCKET_URL = resolveSocketUrl();
const eventHandlers = {
    ReceiveRaceResult: new Set(),
    RaceStatusUpdated: new Set(),
    RaceMoved: new Set(),
    ConnectedUsersCountUpdated: new Set()
};
const statusHandlers = new Set();
const reconnectHandlers = new Set();

let connection = null;
let startPromise = null;
let stopPromise = null;
let subscriberCount = 0;
let visibilityHandlerRegistered = false;
let accessTokenFactory = null;
let lastConnectedUsersCountErrorLogAt = 0;

function nowTime() {
    return new Date().toLocaleTimeString('en-GB', { hour12: false });
}

function notifyStatus(status) {
    statusHandlers.forEach(handler => {
        try {
            handler(status, status === 'Connected!' || status === 'Reconnected!' ? nowTime() : 'n/a');
        } catch (error) {
            console.error('Realtime status handler failed:', error);
        }
    });
}

async function resolveAccessToken() {
    if (!accessTokenFactory) return '';
    try {
        const token = await accessTokenFactory();
        return token || '';
    } catch (error) {
        console.error('Socket.IO access token provider failed:', error);
        return '';
    }
}

function ensureConnection() {
    if (connection) return connection;

    connection = io(SOCKET_URL, {
        autoConnect: false,
        withCredentials: true,
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 30000
    });

    connection.io.on('reconnect_attempt', () => {
        notifyStatus('Reconnecting....');
    });

    connection.io.on('reconnect', () => {
        notifyStatus('Reconnected!');
        reconnectHandlers.forEach(handler => {
            try {
                handler();
            } catch (error) {
                console.error('Realtime reconnect handler failed:', error);
            }
        });
    });

    connection.on('disconnect', () => {
        notifyStatus('DISCONNECTED!');
    });

    connection.on('ReceiveRaceResult', (...args) => {
        eventHandlers.ReceiveRaceResult.forEach(handler => handler(...args));
    });
    connection.on('RaceStatusUpdated', (...args) => {
        eventHandlers.RaceStatusUpdated.forEach(handler => handler(...args));
    });
    connection.on('RaceMoved', (...args) => {
        eventHandlers.RaceMoved.forEach(handler => handler(...args));
    });
    connection.on('ConnectedUsersCountUpdated', (...args) => {
        eventHandlers.ConnectedUsersCountUpdated.forEach(handler => handler(...args));
    });

    return connection;
}

async function startConnection() {
    const activeConnection = ensureConnection();
    if (startPromise) {
        return startPromise;
    }

    if (stopPromise) {
        await stopPromise;
    }

    if (activeConnection.connected) {
        return;
    }

    startPromise = (async () => {
        const token = await resolveAccessToken();
        activeConnection.auth = token ? { token } : {};

        await new Promise((resolve, reject) => {
            const handleConnect = () => {
                cleanup();
                notifyStatus('Connected!');
                resolve();
            };

            const handleError = error => {
                cleanup();
                console.error('Socket.IO connection error:', error);
                notifyStatus('DISCONNECTED!');
                reject(error);
            };

            const cleanup = () => {
                activeConnection.off('connect', handleConnect);
                activeConnection.off('connect_error', handleError);
            };

            activeConnection.on('connect', handleConnect);
            activeConnection.on('connect_error', handleError);
            activeConnection.connect();
        });
    })().finally(() => {
        startPromise = null;
    });

    return startPromise;
}

function requestStopConnection(logPrefix) {
    if (!connection) return Promise.resolve();
    if (stopPromise) return stopPromise;
    if (!connection.connected) {
        return Promise.resolve();
    }

    stopPromise = Promise.resolve()
        .then(() => {
            connection.disconnect();
        })
        .catch(error => {
            console.error(logPrefix, error);
        })
        .finally(() => {
            stopPromise = null;
        });

    return stopPromise;
}

function stopConnectionIfIdle() {
    if (!connection || subscriberCount > 0) return;
    requestStopConnection('Socket.IO disconnect error:');
}

async function refreshConnectionForAuthChange() {
    if (!connection || subscriberCount <= 0) return;
    if (startPromise) {
        try {
            await startPromise;
        } catch (error) {
            // The in-flight connection may have failed because auth was not ready yet.
            console.error('Socket.IO auth refresh waiting on initial connect failed:', error);
        }
    }
    await requestStopConnection('Socket.IO auth-refresh stop error:');
    await startConnection();
}

function handleVisibilityChange() {
    if (!connection) return;
    if (document.hidden) {
        if (connection.connected) {
            requestStopConnection('Socket.IO background stop error:');
        }
        return;
    }
    if (subscriberCount > 0) {
        startConnection();
    }
}

export function subscribeToRealtime({
    onReceiveRaceResult,
    onRaceStatusUpdated,
    onRaceMoved,
    onConnectedUsersCountUpdated,
    onStatusChange,
    onReconnected
} = {}) {
    subscriberCount += 1;
    ensureConnection();

    if (!visibilityHandlerRegistered) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        visibilityHandlerRegistered = true;
    }

    if (onReceiveRaceResult) eventHandlers.ReceiveRaceResult.add(onReceiveRaceResult);
    if (onRaceStatusUpdated) eventHandlers.RaceStatusUpdated.add(onRaceStatusUpdated);
    if (onRaceMoved) eventHandlers.RaceMoved.add(onRaceMoved);
    if (onConnectedUsersCountUpdated) eventHandlers.ConnectedUsersCountUpdated.add(onConnectedUsersCountUpdated);
    if (onStatusChange) statusHandlers.add(onStatusChange);
    if (onReconnected) reconnectHandlers.add(onReconnected);

    startConnection();

    return () => {
        subscriberCount = Math.max(0, subscriberCount - 1);

        if (onReceiveRaceResult) eventHandlers.ReceiveRaceResult.delete(onReceiveRaceResult);
        if (onRaceStatusUpdated) eventHandlers.RaceStatusUpdated.delete(onRaceStatusUpdated);
        if (onRaceMoved) eventHandlers.RaceMoved.delete(onRaceMoved);
        if (onConnectedUsersCountUpdated) eventHandlers.ConnectedUsersCountUpdated.delete(onConnectedUsersCountUpdated);
        if (onStatusChange) statusHandlers.delete(onStatusChange);
        if (onReconnected) reconnectHandlers.delete(onReconnected);

        stopConnectionIfIdle();
    };
}

export function setRealtimeAccessTokenFactory(factory) {
    accessTokenFactory = typeof factory === 'function' ? factory : null;
    refreshConnectionForAuthChange().catch(error => {
        console.error('Socket.IO auth refresh failed:', error);
    });
}

export async function invokeRealtime(methodName, ...args) {
    const activeConnection = ensureConnection();
    if (!activeConnection.connected) {
        await startConnection();
    }

    return new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            reject(new Error(`Socket.IO realtime call timed out: ${methodName}`));
        }, 15000);

        activeConnection.emit(methodName, ...args, response => {
            window.clearTimeout(timeoutId);
            if (response?.ok) {
                resolve(response.result);
                return;
            }
            reject(new Error(response?.error || `Socket.IO realtime call failed: ${methodName}`));
        });
    });
}

export async function getConnectedUsersCount() {
    try {
        const result = await invokeRealtime('GetConnectedUsersCount');
        const parsed = Number(result);
        return Number.isFinite(parsed) ? parsed : null;
    } catch (error) {
        const message = String(error?.message || '');
        const isTimeout = message.includes('Socket.IO realtime call timed out: GetConnectedUsersCount');
        const now = Date.now();

        if (!isTimeout || now - lastConnectedUsersCountErrorLogAt >= 60000) {
            console.error('Socket.IO connected-user count query failed:', error);
            lastConnectedUsersCountErrorLogAt = now;
        }
        return null;
    }
}

export function createRealtimeMapCoalescer(onFlush, keySelector, delayMs = 200) {
    let eventMap = new Map();
    let timer = null;

    const flush = () => {
        if (eventMap.size === 0) return;
        const batched = Array.from(eventMap.values());
        eventMap = new Map();
        onFlush(batched);
    };

    const schedule = () => {
        if (timer !== null) return;
        timer = window.setTimeout(() => {
            timer = null;
            flush();
        }, delayMs);
    };

    return {
        push(event) {
            eventMap.set(keySelector(event), event);
            schedule();
        },
        dispose() {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            flush();
        }
    };
}
